import { DeviationRequestDetail, DeviationRequestDetailApi, DeviationRequestHistoryRecord, DeviationRequestPermissions } from "api/deviation-request-detail";
import { AssignDeviationToModel, ChangeDeviationStatusModel, DeviationDetailUpdateDTO, DeviationRequestAttachmentUpdateDTO, DeviationRequestCreateDTO, DeviationRequestsClient, DeviationRequestUpdateDTO, IAssignDeviationToModel, IChangeDeviationStatusModel, IDeviationDetailUpdateDTO, IDeviationRequestAttachmentUpdateDTO, IDeviationRequestChangeHistoryDetailDTO, IDeviationRequestCreateDTO, IDeviationRequestDetailDTO, IDeviationRequestDetailModel, IDeviationRequestPermissionsModel, IDeviationRequestUpdateBaseDTO, IDeviationRequestUpdateDTO } from "api/swagger/ApiClient";
import { autoinject } from "aurelia-framework";
import { SystemError } from "framework/error_handling/system-error";
import { DeviationDetailType, DeviationRequestStatus, DeviationType, DeviationWorkflowType } from "model/business-enums";
import { AirlineDeviationDetail, AirlineExtendedDeviationDetail, ClassOfTravelDeviationDetail, DeviationDetail, DeviationDetailBase, DeviationRequest, DeviationRequestBase, DeviationRequestWorkflowStrategy, EntityWideDeviationRequest, HotelDeviationDetail, SingleInstanceDeviationRequest } from "model/forms/deviation-request";
import { SystemUserWrapper } from "model/support-types";
import { FileParameter, FileResponse, UploadedFile } from "model/essential-types";
import { BusinessKeywordsMapping } from "./mapping/business-keywords-mapping";
import { BusinessTypesMapping } from "./mapping/business-types-mapping";
import { DeviationRequestDetailMapping } from "./mapping/deviation-request-detail-mapping";
import { EssentialTypesMapping } from "./mapping/essential-types-mapping";
import { DeviationTeamsMapping } from "./mapping/deviation-teams-mapping";

@autoinject
export class DeviationRequestDetailClientApi implements DeviationRequestDetailApi {
  constructor(private deviationRequestsClient: DeviationRequestsClient, private deviationRequestDetailMapping: DeviationRequestDetailMapping, private businessKeywordsMapping: BusinessKeywordsMapping, private essentialTypesMapping: EssentialTypesMapping, private businessTypesMapping: BusinessTypesMapping, private deviationTeamsMapping: DeviationTeamsMapping) {
  }

  async getDeviationRequest(id: number): Promise<DeviationRequestDetail> {
    return this.mapToDeviationRequestDetail(await this.deviationRequestsClient.getById(id));
  }

  async createSingleInstanceTravelRequest(deviationRequest: SingleInstanceDeviationRequest): Promise<DeviationRequestDetail> {
    return this.mapToDeviationRequestDetail(await this.deviationRequestsClient.createDeviationRequest(new DeviationRequestCreateDTO(this.mapSingleInstanceToDeviationRequestCreateDTO(deviationRequest))));
  }

  async createEntityWideTravelRequest(deviationRequest: EntityWideDeviationRequest): Promise<DeviationRequestDetail> {
    return this.mapToDeviationRequestDetail(await this.deviationRequestsClient.createDeviationRequest(new DeviationRequestCreateDTO(this.mapEntityWideToDeviationRequestCreateDTO(deviationRequest))));
  }

  async updateSingleInstanceTravelRequest(id: number, deviationRequest: SingleInstanceDeviationRequest): Promise<DeviationRequestDetail> {
    return this.mapToDeviationRequestDetail(await this.deviationRequestsClient.updateDeviationRequest(id, new DeviationRequestUpdateDTO(this.mapSingleInstanceToDeviationRequestUpdateDTO(deviationRequest))));
  }

  async updateEntityWideTravelRequest(id: number, deviationRequest: EntityWideDeviationRequest): Promise<DeviationRequestDetail> {
    return this.mapToDeviationRequestDetail(await this.deviationRequestsClient.updateDeviationRequest(id, new DeviationRequestUpdateDTO(this.mapEntityWideToDeviationRequestUpdateDTO(deviationRequest))));
  }

  async updateDeviationRequestAttachment(id: number, deviationRequest: DeviationRequest) {
    return this.mapToDeviationRequestDetail(await this.deviationRequestsClient.updateDeviationAttachmentRequest(id, new DeviationRequestAttachmentUpdateDTO(this.mapDeviationToDeviationRequestAttachmentUpdateDTO(deviationRequest))));
  }

  async archiveDeviationRequest(id: number): Promise<DeviationRequestDetail> {
    return this.mapToDeviationRequestDetail(await this.deviationRequestsClient.archiveDeviationRequest(id));
  }

  async restoreDeviationRequest(id: number): Promise<DeviationRequestDetail> {
    return this.mapToDeviationRequestDetail(await this.deviationRequestsClient.unarchiveDeviationRequest(id));
  }

  async changeStatus(id: number, status: DeviationRequestStatus): Promise<DeviationRequestDetail> {
    return this.mapToDeviationRequestDetail(await this.deviationRequestsClient.changeStatus(id, new ChangeDeviationStatusModel({ status: status } as IChangeDeviationStatusModel)));
  }

  async assignTo(id: number, assignToId: number | null): Promise<DeviationRequestDetail> {
    return this.mapToDeviationRequestDetail(await this.deviationRequestsClient.assignTo(id, new AssignDeviationToModel({ assignToId: assignToId } as IAssignDeviationToModel)));
  }

  async uploadAttachment(id: number | null, file: FileParameter): Promise<UploadedFile> {
    return this.essentialTypesMapping.mapUploadedFileDetailToUploadedFile(id != null ? await this.deviationRequestsClient.uploadAttachment2(id, file) : await this.deviationRequestsClient.uploadAttachment(file));
  }

  async downloadAttachment(id: number | null, attachment: UploadedFile): Promise<FileResponse> {
    return id != null ? this.deviationRequestsClient.downloadAttachment2(id, attachment.id) : this.deviationRequestsClient.downloadAttachment(attachment.id);
  }

  getWorkflowStrategy(deviationType: DeviationType, workflowType: DeviationWorkflowType): Promise<DeviationRequestWorkflowStrategy> {
    return this.deviationRequestsClient.getWorkflowStrategy(deviationType, workflowType);
  }

  mapToDeviationRequestDetail(model: IDeviationRequestDetailModel): DeviationRequestDetail {
    return {
      data: this.mapToDeviationRequest(model.deviationRequest),
      history: model.deviationRequest.changeHistories.map(x => this.mapToDeviationRequestHistory(x)),
      permissions: this.mapToDeviationRequestPermissions(model.permissions),
      workflowStrategy: model.workflowStrategy
    }
  }

  mapToDeviationRequest(dto: IDeviationRequestDetailDTO): DeviationRequest {
    const deviationRequest = {
      id: dto.id,
      deviationType: dto.deviationType.value,
      workflowType: dto.workflowType.value,
      requestedBy: this.businessTypesMapping.mapUserToUserContact(dto.requestedBy),
      status: dto.status.value,
      statusChangedOn: dto.statusChangedOn,
      statusChangedBy: dto.statusChangedBy !== null ? this.essentialTypesMapping.mapSystemUserInfoToUserInfo(dto.statusChangedBy) : null,
      validFrom: dto.validFrom,
      validTo: dto.validTo,
      organisationEntity: this.businessKeywordsMapping.mapToOrganizationEntity(dto.organisationEntity),
      detail: this.deviationRequestDetailMapping.mapToDeviationDetail(dto.detail),
      reason: dto.reason,
      alternativeOptions: dto.alternativeOptions,
      riskManagement: dto.riskManagement,
      additionalInfo: dto.additionalInfo,
      epra: dto.epra !== null ? this.essentialTypesMapping.mapSystemUserInfoToUserInfo(dto.epra) : null,
      isSupportedByEpra: dto.isSupportedByEPRA,
      regionDirector: dto.regionDirector != null ? this.deviationTeamsMapping.mapToDeviationTeamRegionDirector(dto.regionDirector) : null,
      attachments: dto.attachments.map(x => this.essentialTypesMapping.mapUploadedFileDetailToUploadedFile(x)),
      assignedOn: dto.assignedOn,
      assignedTo: dto.assignedTo !== null ? this.essentialTypesMapping.mapSystemUserInfoToUserInfo(dto.assignedTo) : null,
      createdBy: this.essentialTypesMapping.mapSystemUserInfoToUserInfo(dto.createdBy),
      createdOn: dto.createdOn,
      modifiedBy: dto.modifiedBy !== null ? this.essentialTypesMapping.mapSystemUserInfoToUserInfo(dto.modifiedBy) : null,
      modifiedOn: dto.modifiedOn,
      archivedBy: dto.archivedBy !== null ? this.essentialTypesMapping.mapSystemUserInfoToUserInfo(dto.archivedBy) : null,
      archivedOn: dto.archivedOn
    } as DeviationRequestBase;
    switch (deviationRequest.deviationType) {
      case DeviationType.SingleInstance:
        return this.mapToSingleInstanceDeviationRequest(dto, deviationRequest);
      case DeviationType.EntityWide:
        return this.mapToEntityWideDeviationRequest(dto, deviationRequest);
      default:
        throw new SystemError(`Deviation type ${deviationRequest.deviationType} is not supported`, "Not supported");
    }
  }

  mapToSingleInstanceDeviationRequest(dto: IDeviationRequestDetailDTO, deviationRequest: DeviationRequestBase): SingleInstanceDeviationRequest {
    return {
      ...deviationRequest,
      lineManagerName: dto.lineManagerName,
      isSupportedByLineManager: dto.isSupportedByLineManager,
      travellers: dto.travellers.map(x => ({ user: this.businessTypesMapping.mapUserToUserContact(x), userId: null } as SystemUserWrapper))
    }
  }

  mapToEntityWideDeviationRequest(dto: IDeviationRequestDetailDTO, deviationRequest: DeviationRequestBase): EntityWideDeviationRequest {
    return {
      ...deviationRequest,
      regionalEntityLeader: this.businessTypesMapping.mapUserToUserContact(dto.regionalEntityLeader),
      isSupportedByRegionalEntityLeader: dto.isSupportedByRegionalEntityLeader,
      sORLeader: dto.sorLeader !== null ? this.businessTypesMapping.mapUserToUserContact(dto.sorLeader) : null,
      isSupportedBySORLeader: dto.isSupportedBySORLeader,
      aviationCOE: dto.aviationCOE !== null ? this.businessTypesMapping.mapUserToUserContact(dto.aviationCOE) : null,
      isSupportedByAviationCOE: dto.isSupportedByAviationCOE,
      cfo: dto.cfo !== null ? this.businessTypesMapping.mapUserToUserContact(dto.cfo) : null,
      isSupportedByCFO: dto.isSupportedByCFO,
      seniorVPHSE: dto.seniorVPHSE !== null ? this.businessTypesMapping.mapUserToUserContact(dto.seniorVPHSE) : null,
      isSupportedBySeniorVPHSE: dto.isSupportedBySeniorVPHSE
    }
  }

  mapToDeviationRequestHistory(dto: IDeviationRequestChangeHistoryDetailDTO): DeviationRequestHistoryRecord {
    return {
      status: dto.status,
      assignedTo: dto.assignedTo !== null ? this.essentialTypesMapping.mapSystemUserInfoToUserInfo(dto.assignedTo) : null,
      comment: dto.comment,
      createdBy: this.essentialTypesMapping.mapSystemUserInfoToUserInfo(dto.createdBy),
      createdOn: dto.createdOn
    }
  }

  mapToDeviationRequestPermissions(model: IDeviationRequestPermissionsModel): DeviationRequestPermissions {
    return {
      canEdit: model.canEdit,
      canEditAttachment: model.canEditAttachment,
      canAssign: model.canAssign,
      canEditWorkflowFields: model.canEditWorkflowFields,
      canSupportByRegionDirector: model.canSupportByRegionDirector,
      canSupport: model.canSupport,
      canReject: model.canReject,
      canArchive: model.canArchive,
      canRestore: model.canUnarchive,
      canRenew: model.canRenew,
      teamMembersToAssign: model.teamMembersToAssign.map(x => this.essentialTypesMapping.mapSystemUserInfoToUserInfo(x))
    }
  }

  mapSingleInstanceToDeviationRequestCreateDTO(model: SingleInstanceDeviationRequest): IDeviationRequestCreateDTO {
    const createDTO = this.mapToDeviationRequestCreateDTO(model);
    createDTO.lineManagerName = model.lineManagerName;
    createDTO.isSupportedByLineManager = model.isSupportedByLineManager;
    createDTO.travellersUniqueIds = model.travellers.map(x => x.user.uniqueid);
    return createDTO;
  }

  mapEntityWideToDeviationRequestCreateDTO(model: EntityWideDeviationRequest): IDeviationRequestCreateDTO {
    const createDTO = this.mapToDeviationRequestCreateDTO(model);
    createDTO.regionalEntityLeaderUniqueId = model.regionalEntityLeader.uniqueid;
    createDTO.isSupportedByRegionalEntityLeader = model.isSupportedByRegionalEntityLeader;
    createDTO.sorLeaderUniqueId = model.sORLeader !== null ? model.sORLeader.uniqueid : null;
    createDTO.isSupportedBySORLeader = model.isSupportedBySORLeader;
    return createDTO;
  }

  mapToDeviationRequestCreateDTO(model: DeviationRequestBase): IDeviationRequestCreateDTO {
    const updateBaseDTO = this.mapToDeviationRequestUpdateBaseDTO(model);
    return {
      ...updateBaseDTO,
      deviationType: model.deviationType,
      workflowType: model.workflowType
    }
  }

  mapToDeviationRequestUpdateBaseDTO(model: DeviationRequestBase): IDeviationRequestUpdateBaseDTO {
    let deviationDetailUpdateDTO: IDeviationDetailUpdateDTO;
    switch (model.detail.detailType) {
      case DeviationDetailType.Airline:
        deviationDetailUpdateDTO = this.mapAirlineToDeviationDetailUpdateDTO(model.detail as AirlineDeviationDetail);
        break;
      case DeviationDetailType.AirlineExtended:
        deviationDetailUpdateDTO = this.mapAirlineExtendedToDeviationDetailUpdateDTO(model.detail as AirlineExtendedDeviationDetail);
        break;
      case DeviationDetailType.ClassOfTravel:
        deviationDetailUpdateDTO = this.mapClassOfTravelToDeviationDetailUpdateDTO(model.detail as ClassOfTravelDeviationDetail);
        break;
      case DeviationDetailType.Hotel:
        deviationDetailUpdateDTO = this.mapHotelToDeviationDetailUpdateDTO(model.detail as HotelDeviationDetail);
        break;
      default:
        throw new SystemError(`Deviation detail type ${model.detail.detailType} is not supported`, "Not supported");
    }
    return {
      requestedByUniqueId: model.requestedBy.uniqueid,
      organisationEntityId: model.organisationEntity.id,
      validFrom: model.validFrom,
      validTo: model.validTo,
      detail: new DeviationDetailUpdateDTO(deviationDetailUpdateDTO),
      reason: model.reason,
      alternativeOptions: model.alternativeOptions,
      riskManagement: model.riskManagement,
      additionalInfo: model.additionalInfo,
      lineManagerName: null,
      isSupportedByLineManager: null,
      epraId: model.epra !== null ? model.epra.id : null,
      isSupportedByEPRA: model.isSupportedByEpra,
      regionDirectorId: model.regionDirector !== null ? model.regionDirector.id : null,
      travellersUniqueIds: null,
      regionalEntityLeaderUniqueId: null,
      isSupportedByRegionalEntityLeader: null,
      sorLeaderUniqueId: null,
      isSupportedBySORLeader: null,
      uploadedFilesIds: model.attachments.map(x => x.id)
    }
  }

  mapAirlineToDeviationDetailUpdateDTO(model: AirlineDeviationDetail): IDeviationDetailUpdateDTO {
    const updateDTO = this.mapToDeviationDetailUpdateDTO(model);
    updateDTO.airlineId = model.airline?.id;
    updateDTO.airlineCustomName = model.airline === null ? model.airlineCustomName : null;
    return updateDTO;
  }

  mapAirlineExtendedToDeviationDetailUpdateDTO(model: AirlineExtendedDeviationDetail): IDeviationDetailUpdateDTO {
    const updateDTO = this.mapToDeviationDetailUpdateDTO(model);
    updateDTO.airlineId = model.airline?.id;
    updateDTO.airlineCustomName = model.airline === null ? model.airlineCustomName : null;
    updateDTO.originCityId = model.originCity?.id;
    updateDTO.originCityCustomName = model.originCity === null ? model.originCityCustomName : null;
    updateDTO.originCountryId = model.originCountry.id;
    updateDTO.destinationCityId = model.destinationCity?.id;
    updateDTO.destinationCityCustomName = model.destinationCity === null ? model.destinationCityCustomName : null;
    updateDTO.destinationCountryId = model.destinationCountry.id;
    return updateDTO;
  }

  mapClassOfTravelToDeviationDetailUpdateDTO(model: ClassOfTravelDeviationDetail): IDeviationDetailUpdateDTO {
    const updateDTO = this.mapToDeviationDetailUpdateDTO(model);
    updateDTO.airlineId = model.airline?.id;
    updateDTO.airlineCustomName = model.airline === null ? model.airlineCustomName : null;
    updateDTO.classOfTravel = model.classOfTravel.value;
    updateDTO.flightTime = model.flightTime.value;
    updateDTO.originCityId = model.originCity?.id;
    updateDTO.originCityCustomName = model.originCity === null ? model.originCityCustomName : null;
    updateDTO.originCountryId = model.originCountry.id;
    updateDTO.destinationCityId = model.destinationCity?.id;
    updateDTO.destinationCityCustomName = model.destinationCity === null ? model.destinationCityCustomName : null;
    updateDTO.destinationCountryId = model.destinationCountry.id;
    return updateDTO;
  }

  mapHotelToDeviationDetailUpdateDTO(model: HotelDeviationDetail): IDeviationDetailUpdateDTO {
    const updateDTO = this.mapToDeviationDetailUpdateDTO(model);
    updateDTO.hotelName = model.hotelName
    updateDTO.cityId = model.city?.id;
    updateDTO.cityCustomName = model.city === null ? model.cityCustomName : null;
    updateDTO.countryId = model.country.id;
    return updateDTO;
  }

  mapToDeviationDetailUpdateDTO(model: DeviationDetailBase): IDeviationDetailUpdateDTO {
    return {
      detailType: model.detailType,
      airlineCustomName: null,
      airlineId: null,
      cityCustomName: null,
      cityId: null,
      classOfTravel: null,
      countryId: null,
      destinationCityCustomName: null,
      destinationCityId: null,
      destinationCountryId: null,
      flightTime: null,
      hotelName: null,
      originCityCustomName: null,
      originCityId: null,
      originCountryId: null
    }
  }

  mapSingleInstanceToDeviationRequestUpdateDTO(model: SingleInstanceDeviationRequest): IDeviationRequestUpdateDTO {
    const updateDTO = this.mapToDeviationRequestUpdateDTO(model);
    updateDTO.lineManagerName = model.lineManagerName;
    updateDTO.isSupportedByLineManager = model.isSupportedByLineManager;
    updateDTO.travellersUniqueIds = model.travellers.map(x => x.user.uniqueid);
    return updateDTO;
  }

  mapEntityWideToDeviationRequestUpdateDTO(model: EntityWideDeviationRequest): IDeviationRequestUpdateDTO {
    const updateDTO = this.mapToDeviationRequestUpdateDTO(model);
    updateDTO.regionalEntityLeaderUniqueId = model.regionalEntityLeader.uniqueid;
    updateDTO.isSupportedByRegionalEntityLeader = model.isSupportedByRegionalEntityLeader;
    updateDTO.sorLeaderUniqueId = model.sORLeader !== null ? model.sORLeader.uniqueid : null;
    updateDTO.isSupportedBySORLeader = model.isSupportedBySORLeader;
    updateDTO.aviationCOEUniqueId = model.aviationCOE?.uniqueid;
    updateDTO.isSupportedByAviationCOE = model.isSupportedByAviationCOE;
    updateDTO.cfoUniqueId = model.cfo?.uniqueid;
    updateDTO.isSupportedByCFO = model.isSupportedByCFO;
    updateDTO.seniorVPHSEUniqueId = model.seniorVPHSE?.uniqueid;
    updateDTO.isSupportedBySeniorVPHSE = model.isSupportedBySeniorVPHSE;
    return updateDTO;
  }

  mapDeviationToDeviationRequestAttachmentUpdateDTO(model: DeviationRequest): IDeviationRequestAttachmentUpdateDTO {
    return {
      uploadedFilesIds: model.attachments.map(x => x.id)
    }
  }

  mapToDeviationRequestUpdateDTO(model: DeviationRequestBase): IDeviationRequestUpdateDTO {
    const updateBaseDTO = this.mapToDeviationRequestUpdateBaseDTO(model);
    return {
      ...updateBaseDTO,
      aviationCOEUniqueId: null,
      isSupportedByAviationCOE: null,
      cfoUniqueId: null,
      isSupportedByCFO: null,
      seniorVPHSEUniqueId: null,
      isSupportedBySeniorVPHSE: null
    }
  }
}
